/**
 * Analytics service - Mixpanel wrapper
 *
 * Privacy: No PII in Mixpanel. Users identified by Cognito user ID only.
 * User debugging happens in admin tools, not Mixpanel.
 */

import mixpanel from 'mixpanel-browser';

import { ANALYTICS_CONFIG, MIXPANEL_TOKEN } from '../config/analytics';

// ============================================================================
// Initialization
// ============================================================================

let isInitialized = false;

/**
 * Initialize Mixpanel. Call once at app startup.
 */
export function initAnalytics(): void {
  if (isInitialized) return;

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: ANALYTICS_CONFIG.debug,
    track_pageview: ANALYTICS_CONFIG.trackPageViews,
    persistence: ANALYTICS_CONFIG.persistence,
  });

  isInitialized = true;

  if (ANALYTICS_CONFIG.debug) {
    globalThis.console.log('[Analytics] Initialized');
  }
}

// ============================================================================
// User Identity
// ============================================================================

/**
 * Identify user by Cognito user ID. Call on sign-in.
 * NO PII - just the user ID.
 */
export function identifyUser(userId: string, authProvider: string): void {
  if (!isInitialized) return;

  mixpanel.identify(userId);
  mixpanel.people.set({
    auth_provider: authProvider,
    $created: new Date().toISOString(),
  });

  if (ANALYTICS_CONFIG.debug) {
    globalThis.console.log('[Analytics] Identified user:', userId);
  }
}

/**
 * Reset user identity. Call on sign-out.
 */
export function resetUser(): void {
  if (!isInitialized) return;

  mixpanel.reset();

  if (ANALYTICS_CONFIG.debug) {
    globalThis.console.log('[Analytics] Reset user');
  }
}

// ============================================================================
// Event Tracking
// ============================================================================

/**
 * Track a custom event
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!isInitialized) return;

  mixpanel.track(event, properties);

  if (ANALYTICS_CONFIG.debug) {
    globalThis.console.log('[Analytics] Track:', event, properties);
  }
}

// ============================================================================
// Typed Event Helpers
// ============================================================================

/** Screen view event */
export function trackScreenView(screenName: string): void {
  track('Screen View', { screen_name: screenName });
}

/** Story selected from library */
export function trackStorySelected(storyId: string, storyTitle: string): void {
  track('Story Selected', { story_id: storyId, story_title: storyTitle });
}

/** Chapter started loading */
export function trackChapterStarted(
  storyId: string,
  nodeId: string,
  chapterNum: number,
  isFree: boolean,
  isEnding: boolean
): void {
  track('Chapter Started', {
    story_id: storyId,
    node_id: nodeId,
    chapter_num: chapterNum,
    is_free: isFree,
    is_ending: isEnding,
  });
}

/** Chapter finished reading (scroll + time threshold met) */
export function trackChapterFinished(
  storyId: string,
  nodeId: string,
  chapterNum: number,
  timeSpentSeconds: number,
  isFree: boolean,
  isEnding: boolean
): void {
  track('Chapter Finished', {
    story_id: storyId,
    node_id: nodeId,
    chapter_num: chapterNum,
    time_spent_seconds: timeSpentSeconds,
    is_free: isFree,
    is_ending: isEnding,
  });
}

/** User made a choice */
export function trackChoiceMade(
  storyId: string,
  nodeId: string,
  chapterNum: number,
  choiceIndex: number,
  isNewBranch: boolean
): void {
  track('Choice Made', {
    story_id: storyId,
    node_id: nodeId,
    chapter_num: chapterNum,
    choice_index: choiceIndex,
    is_new_branch: isNewBranch,
  });
}

/** User explored a different branch (chose differently than before) */
export function trackBranchExplored(
  storyId: string,
  nodeId: string,
  chapterNum: number,
  previousChoiceIndex: number,
  newChoiceIndex: number
): void {
  track('Branch Explored', {
    story_id: storyId,
    node_id: nodeId,
    chapter_num: chapterNum,
    previous_choice_index: previousChoiceIndex,
    new_choice_index: newChoiceIndex,
  });
}

/** Generation wait started (spinner shown) */
export function trackGenerationWaitStarted(storyId: string, nodeId: string): void {
  track('Generation Wait Started', { story_id: storyId, node_id: nodeId });
}

/** Generation wait ended (content loaded) */
export function trackGenerationWaitEnded(
  storyId: string,
  nodeId: string,
  waitSeconds: number
): void {
  track('Generation Wait Ended', {
    story_id: storyId,
    node_id: nodeId,
    wait_seconds: waitSeconds,
  });
}

/** Sign-in flow started */
export function trackSignInStarted(provider: 'apple' | 'google' | 'email'): void {
  track('Sign In Started', { provider });
}

/** Sign-in completed */
export function trackSignInCompleted(
  provider: 'apple' | 'google' | 'email',
  isNewUser: boolean
): void {
  track('Sign In Completed', { provider, is_new_user: isNewUser });
}

/** Sign-in or registration failed */
export function trackAuthError(
  provider: 'apple' | 'google' | 'email',
  step: 'redirect' | 'callback' | 'token_exchange' | 'session_check' | 'registration',
  errorMessage: string,
  errorCode?: string
): void {
  track('Auth Error', {
    provider,
    step,
    error_message: errorMessage,
    error_code: errorCode,
  });
}

/** Purchase flow started */
export function trackPurchaseFlowStarted(productId: string, tokenAmount: number): void {
  track('Purchase Flow Started', { product_id: productId, token_amount: tokenAmount });
}

/** Error shown to user */
export function trackErrorShown(errorCode: string, screenName: string): void {
  track('Error Shown', { error_code: errorCode, screen_name: screenName });
}

/** Node load error shown to reader */
export function trackNodeError(
  storyId: string,
  nodeId: string,
  errorType: 'generation_failed' | 'not_found' | 'unknown',
  httpStatus?: number
): void {
  track('Node Error', {
    story_id: storyId,
    node_id: nodeId,
    error_type: errorType,
    http_status: httpStatus,
  });
}

// ============================================================================
// User Properties
// ============================================================================

/**
 * Update user properties (no PII)
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) return;

  mixpanel.people.set(properties);

  if (ANALYTICS_CONFIG.debug) {
    globalThis.console.log('[Analytics] Set user properties:', properties);
  }
}

/**
 * Increment a numeric user property
 */
export function incrementUserProperty(property: string, value = 1): void {
  if (!isInitialized) return;

  mixpanel.people.increment(property, value);
}
